import { Result } from 'constants/interfaces'
import { passesRecurrenceFilter, findScopedResult } from '../TasksOverview/model/results'

export interface ResultStatusRequest {
  status: number
  tid: string
  task_context_id: string
  result_context_id: string
  recurrence_ts?: string | null
  st_id?: string
  rating?: number
  rework_count?: number
  reservations?: { reasons: string[]; score_deduction: number }
}

/** Omitted occurrence inherits the task; explicit null targets its nonrecurring result. */
export const resolveResultOccurrence = (requested: string | null | undefined, taskOccurrence?: string | null) =>
  requested === undefined ? taskOccurrence ?? null : requested

/** Unlike overview filtering, a nonrecurring mutation must never borrow a recurring row. */
export function findResultForMutation(
  results: Result[] = [],
  stId: string,
  contextId: string,
  occurrence: string | null
) {
  const scoped =
    occurrence === null
      ? results.filter(
          (result) =>
            !result.context_id_recurrence_ts ||
            result.context_id_recurrence_ts === result.context_id ||
            result.context_id_recurrence_ts === `${result.context_id}_null` ||
            result.context_id_recurrence_ts === `${result.context_id}_undefined`
        )
      : results

  return findScopedResult(scoped, stId, contextId, occurrence)
}

/** Upsert one context/occurrence, retaining other stores and historical results. */
export function upsertTaskResult(results: Result[], updated: Result): Result[] {
  const occurrenceOf = (result: Result) => {
    const prefix = `${result.context_id}_`
    const recurrence = result.context_id_recurrence_ts?.slice(prefix.length)

    return recurrence && recurrence !== 'null' && recurrence !== 'undefined' ? recurrence : null
  }

  const recurring = occurrenceOf(updated)

  const sameIdentity = (result: Result) =>
    result.st_id === updated.st_id &&
    result.context_id === updated.context_id &&
    (result.context_id_recurrence_ts ? occurrenceOf(result) === recurring : passesRecurrenceFilter(result, recurring))

  const index = results.findIndex(sameIdentity)

  if (index < 0) return [...results, updated]
  return results.flatMap((result, position) => (position === index ? [updated] : sameIdentity(result) ? [] : [result]))
}

/** Merge a scoped fetch into the current store, keeping other occurrences and newer in-flight saves. */
export function mergeFetchedTaskResults(
  current: Result[],
  fetched: Result[],
  atRequestStart: Result[],
  recurrenceTs?: string | null
): Result[] {
  return fetched.reduce((merged, result) => {
    const incoming = result.context_id_recurrence_ts
      ? result
      : {
          ...result,
          context_id_recurrence_ts: `${result.context_id}_${recurrenceTs ?? null}`,
        }
    const prefix = `${incoming.context_id}_`
    const occurrence = incoming.context_id_recurrence_ts!.slice(prefix.length)
    const scope = occurrence === 'null' || occurrence === 'undefined' ? null : occurrence
    const latest = findScopedResult(merged, incoming.st_id, incoming.context_id, scope)
    const previous = findScopedResult(atRequestStart, incoming.st_id, incoming.context_id, scope)

    if (
      latest &&
      latest !== previous &&
      (!incoming.updated_at_ts || Number(latest.updated_at_ts || 0) >= Number(incoming.updated_at_ts))
    ) {
      return merged
    }
    return upsertTaskResult(merged, incoming)
  }, current)
}
